import { Timestamp } from '@weblite/ts-action'
import { RoomTypes } from '@weblite/ts-action/room'
import {
  CallType,
  CancelCall,
  reqStartCall,
  reqUnscheduleCall,
  reqUpdateCall,
  StartCall,
  UpdateCall,
} from '@weblite/ts-action/media-proxy'
import {
  showCallDeletedSnackbar,
  showCallEditedSnackbar,
  showErrorSnackbar,
} from '$comp/snackbars'
import { HallCallType } from '$comp/modals/create-hall-call/choose-call-time'
import { getHallPushData, getPvPushData, sendTextMessage } from '$logic/message'
import { createNewInviteLink, getAllInviteLinks, isExpired } from '$logic/hall'
import { formatToTimeDay } from '$func/date'
import { MAX_DATE } from '$func/time'
import { request } from '$setup/ws'
import { t } from '$i18n'
import { convertInviteLinkToReadableFormat } from '$logic/hall/helper'

export async function createScheduledCallNotificationMessage(
  roomId: string,
  callType: string,
  callTime: Date,
  roomType: RoomTypes,
  name?: string,
) {
  const startsAt = formatToTimeDay(callTime)

  const inviteLinks = await getAllInviteLinks(roomId)
  const inviteLink =
    inviteLinks?.find(link => !link.isRevoked && !isExpired(link)) ||
    (await createNewInviteLink(roomId, +MAX_DATE, Infinity))
  const inviteLinkWithPrefix = inviteLink
    ? convertInviteLinkToReadableFormat(inviteLink.link)
    : ''

  return t('call.scheduledCallNotificationMessage')(
    t(`call.${callType}`),
    startsAt,
    t(`common.${roomType === 'CHANNEL' ? 'channel' : 'group'}`),
    name,
    inviteLinkWithPrefix,
  )
}

export async function scheduleCall({
  roomId,
  roomType,
  callType,
  shouldSendNotification,
  callTime,
  name,
}: {
  roomId: string
  roomType: RoomTypes
  callType: HallCallType
  shouldSendNotification: boolean
  callTime: Date
  name?: string
}) {
  // TODO: Analytics
  const notificationMessage = shouldSendNotification
    ? await createScheduledCallNotificationMessage(
        roomId,
        callType,
        callTime,
        roomType,
        name,
      )
    : null

  const { error, payload } = await request<StartCall>(
    reqStartCall({
      roomId,
      roomType,
      callType,
      push: roomType === 'PV' ? getPvPushData() : await getHallPushData(roomId),
      startingAt: callTime,
      options: {
        name,
      },
    }),
  )

  if (error) {
    // TODO: Show error toast
  } else {
    const { error: resError } = payload
    if (resError) {
      if (resError === 'USER_BANNED')
        showErrorSnackbar(t('snackbar.call.userBanned')(t(`call.${callType}`)))
    }

    if (notificationMessage)
      sendTextMessage(notificationMessage, undefined, roomId, roomType)
  }
}

export async function unscheduleCall(
  roomId: string,
  roomType: RoomTypes,
  callType: CallType,
) {
  const { error } = await request<CancelCall>(
    reqUnscheduleCall({
      roomId,
      roomType,
      callType,
    }),
  )
  if (error) {
    // TODO: show error toast
  } else {
    showCallDeletedSnackbar(t(`call.${callType}`))
  }
}

export async function updateScheduledCall(
  roomId: string,
  roomType: RoomTypes,
  callType: CallType,
  shouldSendNotification: boolean,
  name: string | undefined,
  startedAt: Timestamp,
  newName?: string,
  newStartedAt?: Timestamp,
) {
  if (newStartedAt || newName) {
    const notificationMessage = shouldSendNotification
      ? await createScheduledCallNotificationMessage(
          roomId,
          callType,
          new Date(newStartedAt || startedAt),
          roomType,
          newName || name,
        )
      : null
    const { error } = await request<UpdateCall>(
      reqUpdateCall({
        roomId,
        roomType,
        callType,
        update: { name: newName, startedAt: newStartedAt },
      }),
    )
    if (error) {
      // TODO: show error toast
    } else {
      if (notificationMessage)
        sendTextMessage(notificationMessage, undefined, roomId, roomType)
      showCallEditedSnackbar(t(`call.${callType}`))
    }
  }
}
